import { useEffect, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import * as Sentry from "@sentry/react";
import sendServerLogging from "../../utils/sendServerLogging";
import userJSON from "../../utils/userJSON";
import { useLauncherDock } from "../../utils/launcherDock";
import { closePylonChat, openPylonChat } from "../../utils/pylonChat";
import { getPylonUnreadCount, subscribeToPylonUnreadCount } from "../../lib/pylonA11y";

// Custom launcher for the Pylon support chat. Replaces Pylon's default chat
// bubble (kept hidden in App.js) so the corner slot carries Knowtex branding.
export default function SupportButton() {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const dock = useLauncherDock();
  const unreadCount = useSyncExternalStore(subscribeToPylonUnreadCount, getPylonUnreadCount);
  const unreadSuffix =
    unreadCount > 0 ? `, ${unreadCount} unread message${unreadCount === 1 ? "" : "s"}` : "";

  // App.js owns the "pylon-chat-open" body class; mirror it so the launcher
  // can toggle the chat window the way Pylon's own bubble used to.
  useEffect(() => {
    const syncChatOpen = () => {
      setIsChatOpen(document.body.classList.contains("pylon-chat-open"));
      setIsCompact(
        document.body.classList.contains("smart-chart-open") ||
          document.body.classList.contains("edit-patient-modal-open")
      );
    };

    syncChatOpen();
    const observer = new MutationObserver(syncChatOpen);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const handleClick = async () => {
    if (isChatOpen) {
      closePylonChat();
      return;
    }

    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "CLICKED_KNOWTEX_SUPPORT",
        eventData: {
          pathname: window.location.pathname,
          patientId: "",
          mrn: "",
        },
      },
    });

    openPylonChat();
    await Sentry?.getReplay()?.flush();
  };

  const launcher = (
    <button
      id="navigate_contact_us"
      type="button"
      onClick={handleClick}
      aria-label={`${isChatOpen ? "Close" : "Open"} Knowtex Support chat${unreadSuffix}`}
      aria-expanded={isChatOpen}
      data-compact={isCompact ? "true" : "false"}
      data-testid="knowtex-support"
      /* No px-4 / py-3 / gap-2 here: Bootstrap (loaded from a CDN in
         index.html) defines those same class names with !important, which
         beats the padding and gap tokens in index.css and, through
         border-box, floors the height. Spacing comes from
         .knowtex-support-launcher instead. */
      className="knowtex-support-launcher fixed z-[99999] inline-flex items-center justify-center rounded-full bg-accent2 text-sm font-semibold text-white transition duration-200 hover:scale-105 hover:bg-purple2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ktx-purple focus-visible:ring-offset-2"
    >
      {isChatOpen ? (
        <svg
          className="h-5 w-5 shrink-0"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 20 20"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="m5 8 5 5 5-5"
          />
        </svg>
      ) : (
        <svg
          className="h-5 w-5 shrink-0"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 20 18"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M16 5h2a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-2v3l-4-3H8m4-13H2a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h2v3l4-3h4a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1Z"
          />
        </svg>
      )}
      {!isCompact ? <span className="whitespace-nowrap">Knowtex Support</span> : null}
    </button>
  );

  return dock ? createPortal(launcher, dock) : launcher;
}
